/**
 * Which dashboard tiles a person sees, decided by the permission matrix.
 *
 * The same shape as `visibleNav` in `admin-nav.ts`, and for the same reason
 * (R-120): the dashboard is a React component, nothing in the suite renders
 * one, so a rule left inline is a rule a mutation can delete without any test
 * noticing. The rule is a function here so it can be falsified.
 *
 * **What went wrong.** The dashboard showed Revenue, Cost of goods, Profit and
 * a margin percentage, computed in the browser from
 * `published_products.base_cost_cents`, with no permission check anywhere on
 * the screen. Its nav entry declares `module: null`, meaning everybody
 * internal. Staff hold `jobs.view` and reach `/admin` for the job board, and
 * the first sidebar item handed them the margin. Constitution §12, §14 and
 * FR-502. R-235.
 *
 * **The modules are the client's, not mine.** `role_permissions` already
 * carries `costs` (staff false, manager and admin true) and `reports` (staff
 * false, manager and admin true). Cost of goods and Profit are cost. Revenue is
 * a report figure. Neither gate is invented for this screen.
 *
 * **Hiding a tile is a courtesy.** `/api/data` strips `base_cost_cents` for a
 * caller without `costs:view` before it leaves the server, and the database
 * refuses underneath that. This decides what is worth drawing, never what is
 * allowed.
 */
import { formatCents } from './money';

/** A headline figure on the dashboard, and the permission that reveals it. */
export interface StatTile {
  label: string;
  value: string;
  sub: string;
  /** The module whose `view` permission reveals this tile, or null for everybody. */
  module: string | null;
}

export function visibleStats(
  tiles: readonly StatTile[],
  can: Record<string, string[]>,
): StatTile[] {
  const mayView = (module: string) => (can[module] ?? []).includes('view');
  return tiles.filter((t) => t.module === null || mayView(t.module));
}

/** The headline figures, as `/api/dashboard` sends them. Cents. */
export interface HeadlineFigures {
  /** Orders in range that count as sales: paid, not cancelled, not refunded. Spec 015 FR-1540. */
  orders: number;
  /** Every one of those orders, WITHOUT GST. The base the Revenue tile shows. */
  revenue_cents: number;
  /**
   * The GST taken out of the subtotals to get `revenue_cents`. The ATO's money,
   * not the business's, and cost of goods is without GST too. Tracker #9.
   */
  gst_cents?: number | null;
  /** Null when this caller may not see money. Not zero: zero is a figure. */
  cost_cents: number | null;
  /** The part of `revenue_cents` a supplier cost is known for. Null with cost. */
  costed_revenue_cents: number | null;
  /** The server's own answer to "may this caller see cost". */
  show_cost: boolean;
  /** Quotes submitted or awaiting proof. */
  quotes_awaiting: number;
}

/**
 * The four headline tiles, before the permission gate.
 *
 * **Why the copy lives here and not on the screen.** Guy read the three money
 * tiles the only way three money figures side by side can be read, and said
 * they did not add up. He was right:
 *
 *     Revenue        $4,169.29
 *     Cost of goods  $1,439.57   "Known for 63% of the revenue"
 *     Profit         $1,187.98   "45% margin on the costed 63%"
 *
 * $4,169.29 less $1,439.57 is $2,729.72. Every figure was correct and they were
 * on two bases. Revenue is all 30 orders. Cost and profit are the $2,627.55 of
 * it that a supplier cost is known for. **That number appeared nowhere**, so
 * the only subtraction available to the reader was the wrong one, and a
 * percentage is not a substitute: nobody checks a dashboard by multiplying.
 *
 * So the captions name the costed revenue in dollars. The arithmetic now closes
 * on the face of the cards, and a reader with a calculator can prove it.
 *
 * The percentage is DERIVED from those same two figures rather than taken from
 * the server, so the words and the numbers cannot drift apart. The server used
 * the line revenue as its denominator while the caption said "of the revenue",
 * and an order with no lines counts in one and not the other.
 *
 * It is a function for the reason the permission rule above is a function
 * (R-120, R-235): nothing in the suite renders this component, so copy left
 * inline is copy no test can see. That is how this shipped.
 */
export function headlineTiles(f: HeadlineFigures): StatTile[] {
  const orders = f.orders.toLocaleString('en-AU');
  const revenue: StatTile = {
    label: 'Revenue',
    value: formatCents(f.revenue_cents),
    // Says GST is out, and how much, because a revenue figure that does not
    // match the order totals is the next thing somebody reports. Tracker #9.
    sub: f.gst_cents != null && f.gst_cents > 0
      ? `${orders} orders, excluding ${formatCents(f.gst_cents)} GST, unpaid, cancelled and refunded`
      : `${orders} orders, excluding GST, unpaid, cancelled and refunded`,
    module: 'reports',
  };
  const quotes: StatTile = {
    label: 'Quotes to answer',
    value: String(f.quotes_awaiting),
    sub: 'Submitted or awaiting proof',
    module: null,
  };

  const cost = f.cost_cents;
  const costed = f.costed_revenue_cents;
  if (!f.show_cost || cost == null || costed == null) {
    return [
      revenue,
      { label: 'Orders', value: orders, sub: 'In this range', module: null },
      quotes,
    ];
  }

  const profit = costed - cost;
  // Whole percentages, both of them, so "45% of 63%" cannot be read off two
  // different roundings of the same pair of numbers.
  const covered = f.revenue_cents > 0
    ? Math.round((costed / f.revenue_cents) * 100)
    : 0;
  const margin = costed > 0 ? Math.round((profit / costed) * 100) : null;

  // At full coverage the costed revenue IS the revenue tile, so naming it again
  // is noise and the subtraction already closes against the card beside it.
  const whole = covered >= 100 && costed === f.revenue_cents;

  return [
    revenue,
    {
      label: 'Cost of goods',
      value: formatCents(cost),
      sub: whole
        ? 'What the goods cost us'
        : `What the goods cost on ${formatCents(costed)} of the revenue, which is ${covered}%`,
      module: 'costs',
    },
    {
      label: 'Profit',
      value: formatCents(profit),
      sub: margin == null
        ? 'No costed sales in this range'
        : `${formatCents(costed)} less ${formatCents(cost)}, a ${margin}% margin`,
      module: 'costs',
    },
    quotes,
  ];
}

/**
 * One order line, as much of it as the cost sum needs.
 *
 * `published_id` is nullable on the real row: a line can exist without a
 * published product behind it. Such a line has no cost that can be looked up,
 * and the old sum counted it as zero.
 */
export interface CostLine {
  published_id: string | null;
  qty: number;
}

/**
 * A published row as the browser receives it.
 *
 * `base_cost_cents` is optional **because the server removes it**, not because
 * it is nullable in the database. Constitution §14 says absent rather than
 * null, and this type is where that contract reaches the browser.
 */
export interface CostSource {
  id: string;
  base_cost_cents?: number | null;
}

/**
 * The cost of goods for a set of order lines, or null when it cannot be known.
 *
 * Null is not zero. The old sum used `p ? p.base_cost_cents * item.qty : 0`,
 * which reads as a total and is a floor: an unmatched line silently contributed
 * nothing, and a stripped column contributed `undefined * qty`, which is `NaN`
 * and poisons the whole sum. Both would render under a heading that says
 * "Cost of goods".
 *
 * So a line that cannot be costed makes the answer null, and the caller drops
 * the tile rather than showing a number that is not one. This is the same rule
 * `formatCentsOrNone` applies to a missing price (R-315).
 */
export function costOfGoods(
  lines: readonly CostLine[],
  published: readonly CostSource[],
): number | null {
  let total = 0;
  for (const line of lines) {
    if (line.published_id === null) return null;
    const row = published.find((p) => p.id === line.published_id);
    if (!row) return null;
    const cost = row.base_cost_cents;
    if (typeof cost !== 'number' || !Number.isFinite(cost)) return null;
    total += cost * line.qty;
  }
  return total;
}
